/* eslint-disable boundaries/element-types */
import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  getSortedUniqueValues,
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import {
  FilterContainer,
  FilterHeaderButton,
  LabelAndInputContainer,
  MultiSelectInput,
  RelativeWrap,
  RemoveFilterButton,
  StyledCloseIcon,
} from 'features/assetDatabase'
import React, { useEffect, useMemo, useRef, useState } from 'react'

export const MultiSelectFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  navigate,
  disableUrlFilters,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isInputVisible, setInputVisible] = useState(true)
  const [currentFilterValues, setCurrentFilterValues] = useState<string[]>([])
  const columnFilterValue = column.getFilterValue() as string[]

  useEffect(() => {
    if (columnFilterValue && columnFilterValue !== currentFilterValues) {
      setCurrentFilterValues(columnFilterValue)
      setInputVisible(false)
    }
  }, [columnFilterValue, currentFilterValues])

  const sortedUniqueValues = useMemo(
    () => getSortedUniqueValues(column),
    [column]
  )

  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target
    const newValues = checked
      ? [...currentFilterValues, value]
      : currentFilterValues.filter((v) => v !== value)

    column.setFilterValue(newValues.length ? newValues : undefined)
    setCurrentFilterValues(newValues)

    // Update URL parameters (only if URL filters are enabled)
    if (!disableUrlFilters) {
      if (newValues.length > 0) {
        navigate({
          to: '.',
          search: (prev) => ({
            ...prev,
            [column.id]: newValues,
          }),
        })
      } else {
        removeFilter(column, setActiveFilters, navigate)
      }
    }
  }

  const handleRemoveFilterValue = (value: string) => {
    const newValues = currentFilterValues.filter((v) => v !== value)

    column.setFilterValue(newValues.length ? newValues : undefined)
    setCurrentFilterValues(newValues)

    // Update URL parameters
    if (newValues.length > 0) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          [column.id]: newValues,
        }),
      })
    } else {
      removeFilter(column, setActiveFilters, navigate)
    }
  }

  const handleOutsideClick = () => {
    if (!currentFilterValues || !currentFilterValues.length) {
      removeFilter(column, setActiveFilters, navigate)
    } else {
      setInputVisible(false)
    }
  }

  useClickOutside(filterContainerRef, handleOutsideClick)

  return (
    <RelativeWrap>
      <FilterHeaderButton
        onClick={() => setInputVisible((visible) => !visible)}>
        {columnHeader}
        {currentFilterValues.map((value) => (
          <RemoveFilterButton
            key={value}
            onClick={() => handleRemoveFilterValue(value)}>
            {value}
            <StyledCloseIcon />
          </RemoveFilterButton>
        ))}
        <RemoveFilterButton
          onClick={() => removeFilter(column, setActiveFilters, navigate)}>
          <StyledCloseIcon />
        </RemoveFilterButton>
      </FilterHeaderButton>
      {isInputVisible && (
        <FilterContainer
          ref={filterContainerRef}
          className={isInputVisible ? 'input-visible' : ''}>
          {sortedUniqueValues.map((value) => (
            <LabelAndInputContainer key={value}>
              <MultiSelectInput
                type='checkbox'
                value={value}
                checked={currentFilterValues.includes(value)}
                onChange={handleSelectChange}
              />
              {value}
            </LabelAndInputContainer>
          ))}
        </FilterContainer>
      )}
    </RelativeWrap>
  )
}
